/**
 * Tab: Canais de notificação — CRUD + Test.
 *
 * Cada tipo tem form de config próprio. `voice` é o único com escalação: a
 * ORDEM dos números é a ordem em que o telefone toca, então o form mostra a
 * fila numerada enquanto a pessoa digita — a informação mais importante desse
 * cadastro não é quem está na lista, é quem é o primeiro.
 */

import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Mail,
  MessageCircle,
  MessageSquare,
  Pencil,
  Phone,
  Plus,
  Send,
  Trash2,
  Webhook,
} from "lucide-react";
import toast from "react-hot-toast";

import { channelsApi } from "@/api/alerts";
import { apiErrorMessage } from "@/api/client";
import { alertQk, useNotificationChannels } from "@/hooks/queries";
import type {
  ChannelKind,
  NotificationChannel,
  NotificationChannelCreate,
} from "@/types";

import { Button } from "@/components/Button";
import { DataTable, type Column } from "@/components/DataTable";
import { Modal } from "@/components/Modal";
import { Field, Input, Select } from "@/components/Field";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Badge } from "@/components/Badge";
import { Tip } from "@/components/Tip";

const KIND_ICON: Record<ChannelKind, typeof Mail> = {
  email:    Mail,
  webhook:  Webhook,
  telegram: MessageCircle,
  whatsapp: MessageSquare,
  voice:    Phone,
};

// Como cada campo de lista é editado no formulário. Tem que casar com o
// `parseConfig` lá embaixo: o que a gente junta aqui é o que ele separa na
// volta, e divergir entre os dois é a forma silenciosa de perder um
// destinatário no meio do caminho.
const SEPARADOR_DE_LISTA: Record<string, string> = {
  para:     "\n",   // telefones do canal de voz: um por linha
  to:       "\n",
  to_addrs: ", ",   // destinatários de e-mail: separados por vírgula
};

const KIND_LABEL: Record<ChannelKind, string> = {
  email:    "Email (SMTP)",
  webhook:  "Webhook (Slack/Discord/etc)",
  telegram: "Telegram",
  whatsapp: "WhatsApp",
  voice:    "Chamada telefônica",
};

export function ChannelsTab({ tenantId }: { tenantId?: string }) {
  const channels = useNotificationChannels(tenantId);
  const qc = useQueryClient();

  const [isCreating, setIsCreating] = useState(false);
  const [editTarget, setEditTarget] = useState<NotificationChannel | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<NotificationChannel | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: (p: NotificationChannelCreate) => channelsApi.create(p, tenantId),
    onSuccess: () => {
      toast.success("Canal criado");
      // Invalida prefix-only ['notification-channels'] pra pegar TODAS as variações
      // de tenantId (super_admin pode estar com filtro tenant=X).
      void qc.invalidateQueries({ queryKey: ["notification-channels"] });
      setIsCreating(false);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, p }: { id: string; p: NotificationChannelCreate }) =>
      channelsApi.update(id, { name: p.name, config: p.config, enabled: p.enabled }),
    onSuccess: () => {
      toast.success("Canal atualizado");
      // Invalida prefix-only ['notification-channels'] pra pegar TODAS as variações
      // de tenantId (super_admin pode estar com filtro tenant=X).
      void qc.invalidateQueries({ queryKey: ["notification-channels"] });
      setEditTarget(null);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => channelsApi.remove(id),
    onSuccess: () => {
      toast.success("Canal deletado");
      // Invalida prefix-only ['notification-channels'] pra pegar TODAS as variações
      // de tenantId (super_admin pode estar com filtro tenant=X).
      void qc.invalidateQueries({ queryKey: ["notification-channels"] });
      setDeleteTarget(null);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  async function handleTest(ch: NotificationChannel) {
    setTestingId(ch.id);
    try {
      const r = await channelsApi.test(ch.id);
      if (r.success) {
        toast.success(`Teste enviado via ${ch.name}`);
      } else {
        toast.error(`Falhou: ${r.error ?? "erro desconhecido"}`);
      }
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setTestingId(null);
    }
  }

  const columns: Column<NotificationChannel>[] = [
    {
      key: "name",
      header: "Nome",
      cell: (r) => {
        const Icon = KIND_ICON[r.kind as ChannelKind];
        return (
          <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-slate-400 dark:text-slate-500" />
            <span className="font-medium text-slate-900 dark:text-slate-100">{r.name}</span>
          </div>
        );
      },
    },
    {
      key: "kind",
      header: "Tipo",
      cell: (r) => <Badge tone="brand">{KIND_LABEL[r.kind as ChannelKind] ?? r.kind}</Badge>,
    },
    {
      key: "enabled",
      header: "Status",
      cell: (r) =>
        r.enabled ? (
          <Badge tone="success">ativo</Badge>
        ) : (
          <Badge tone="default">desabilitado</Badge>
        ),
      hideOnMobile: true,
    },
    {
      key: "actions",
      header: "",
      align: "right",
      cell: (r) => (
        <div className="flex justify-end gap-1">
          <Button
            size="sm"
            variant="ghost"
            icon={<Send className="h-4 w-4" />}
            loading={testingId === r.id}
            onClick={() => handleTest(r)}
          >
            Testar
          </Button>
          <Button size="sm" variant="ghost" icon={<Pencil className="h-4 w-4" />} onClick={() => setEditTarget(r)}>
            Editar
          </Button>
          <Button size="sm" variant="ghost" icon={<Trash2 className="h-4 w-4 text-red-500" />} onClick={() => setDeleteTarget(r)} />
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button icon={<Plus className="h-4 w-4" />} onClick={() => setIsCreating(true)}>
          Novo canal
        </Button>
      </div>

      <DataTable
        data={channels.data}
        columns={columns}
        loading={channels.isLoading}
        rowKey={(r) => r.id}
        emptyTitle="Nenhum canal configurado"
        emptyDescription="Adicione email, webhook ou telegram pra começar a receber alertas."
      />

      <ChannelFormModal
        key={isCreating ? "create" : `edit-${editTarget?.id ?? "none"}`}
        open={isCreating || !!editTarget}
        onClose={() => {
          setIsCreating(false);
          setEditTarget(null);
        }}
        title={isCreating ? "Novo canal" : "Editar canal"}
        initial={editTarget}
        submitting={createMut.isPending || updateMut.isPending}
        onSubmit={(p) => {
          if (editTarget) updateMut.mutate({ id: editTarget.id, p });
          else            createMut.mutate(p);
        }}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Deletar canal?"
        message={
          deleteTarget
            ? `Vai apagar '${deleteTarget.name}' e todas as subscriptions vinculadas.`
            : ""
        }
        confirmLabel="Deletar"
        loading={deleteMut.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget.id)}
      />
    </div>
  );
}

// ---------- form modal ------------------------------------------------------

function ChannelFormModal({
  open, onClose, title, initial, submitting, onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  initial?: NotificationChannel | null;
  submitting?: boolean;
  onSubmit: (p: NotificationChannelCreate) => void;
}) {
  const [name, setName] = useState(initial?.name ?? "");
  const [kind, setKind] = useState<ChannelKind>((initial?.kind as ChannelKind) ?? "email");
  const [config, setConfig] = useState<Record<string, string>>({});
  const [enabled, setEnabled] = useState(initial?.enabled ?? true);

  useEffect(() => {
    setName(initial?.name ?? "");
    setKind((initial?.kind as ChannelKind) ?? "email");
    setEnabled(initial?.enabled ?? true);
    // Config vira texto pra editar nos inputs — mas LISTA não vira JSON.
    //
    // `JSON.stringify` num array devolvia `["+5511...","+5511..."]` dentro do
    // campo. O formulário então mostrava os números como uma linha só de
    // JSON, a validação de E.164 reprovava tudo, e trocar um número exigia
    // redigitar todos. Valia pros telefones do canal de voz e, desde antes,
    // pros destinatários de e-mail.
    //
    // Objeto de verdade (headers do webhook) continua como JSON: ali o JSON
    // É a forma de edição.
    const c: Record<string, string> = {};
    const initialConfig = (initial?.config ?? {}) as Record<string, unknown>;
    for (const [k, v] of Object.entries(initialConfig)) {
      if (Array.isArray(v)) {
        c[k] = v.map((x) => String(x ?? "").trim()).filter(Boolean)
                .join(SEPARADOR_DE_LISTA[k] ?? ", ");
      } else if (v !== null && typeof v === "object") {
        c[k] = JSON.stringify(v);
      } else {
        c[k] = String(v ?? "");
      }
    }
    setConfig(c);
  }, [initial]);

  function setField(k: string, v: string) {
    setConfig({ ...config, [k]: v });
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const cfg = parseConfig(kind, config);
    onSubmit({ name, kind, config: cfg, enabled });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button type="submit" form="channel-form" loading={submitting}>
            Salvar
          </Button>
        </>
      }
    >
      <form id="channel-form" onSubmit={handleSubmit} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Nome" required>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="Equipe ops"
            />
          </Field>
          <Field label="Tipo" required>
            <Select
              value={kind}
              onChange={(e) => setKind(e.target.value as ChannelKind)}
              options={[
                { value: "email",    label: "Email (SMTP)" },
                { value: "webhook",  label: "Webhook" },
                { value: "telegram", label: "Telegram" },
                { value: "whatsapp", label: "WhatsApp" },
                { value: "voice",    label: "Chamada telefônica" },
              ]}
              disabled={!!initial}
            />
          </Field>
        </div>

        {kind === "email" && (
          <EmailFields config={config} setField={setField} />
        )}
        {kind === "webhook" && (
          <WebhookFields config={config} setField={setField} />
        )}
        {kind === "telegram" && (
          <TelegramFields config={config} setField={setField} />
        )}
        {kind === "whatsapp" && (
          <WhatsAppFields config={config} setField={setField} />
        )}
        {kind === "voice" && (
          <VoiceFields config={config} setField={setField} />
        )}

        <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="h-4 w-4 rounded border-surface-300 dark:border-slate-700 text-brand-600"
          />
          Canal ativo
        </label>
      </form>
    </Modal>
  );
}

// ---------- forms por tipo --------------------------------------------------

function EmailFields({ config, setField }: { config: Record<string,string>; setField: (k: string, v: string) => void }) {
  /*
   * Um campo só, e é de propósito.
   *
   * Aqui havia seis: host, porta, usuário, senha, remetente e STARTTLS. Nada
   * disso é decisão de quem cadastra um canal — é infraestrutura da
   * plataforma, igual pra todos os clientes, e agora vive nas variáveis de
   * ambiente junto com o resto (mesmo desenho do WhatsApp, onde o canal
   * sempre guardou só o número).
   *
   * O que sobrou é a única pergunta que o cliente sabe responder: quem
   * recebe. Formulário que pede senha de SMTP pra dizer "me avisa neste
   * e-mail" transfera pro usuário um problema que é nosso.
   */
  return (
    <Field
      label={
        <>
          Destinatários
          <Tip label="Destinatários">
            Um ou mais endereços, separados por vírgula. Todos recebem cada
            alerta deste canal.
          </Tip>
        </>
      }
      required
    >
      <Input
        value={config.to_addrs ?? ""}
        onChange={(e) => setField("to_addrs", e.target.value)}
        required
        placeholder="op@cliente.com.br, gerente@cliente.com.br"
      />
      <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
        O envio sai pelo servidor de e-mail do EnvPulse. Use o botão{" "}
        <strong>Testar</strong> depois de salvar para confirmar a entrega.
      </p>
    </Field>
  );
}

function WebhookFields({ config, setField }: { config: Record<string,string>; setField: (k: string, v: string) => void }) {
  return (
    <>
      <Field
        required
        label={
              <>
                URL
                <Tip label="URL">
                  Serve pra Slack, Discord, Mattermost, Teams — qualquer endpoint que
                  aceite um POST com corpo JSON.
                </Tip>
              </>
            }
      >
        <Input value={config.url ?? ""} onChange={(e) => setField("url", e.target.value)} required placeholder="https://hooks.slack.com/services/..." />
      </Field>
      <Field label="Método">
        <Select
          value={config.method ?? "POST"}
          onChange={(e) => setField("method", e.target.value)}
          options={[
            { value: "POST", label: "POST" },
            { value: "PUT",  label: "PUT" },
          ]}
        />
      </Field>
      <Field label="Headers extras (JSON)">
        <Input value={config.headers ?? ""} onChange={(e) => setField("headers", e.target.value)} placeholder='{"X-Auth":"..."}' />
      </Field>
    </>
  );
}

function TelegramFields({ config, setField }: { config: Record<string,string>; setField: (k: string, v: string) => void }) {
  return (
    <>
      <Field
        label={
          <>
            Bot token
            <Tip label="Bot token">
              Crie o bot conversando com @BotFather no Telegram — ele devolve o
            token no fim da conversa.
            </Tip>
          </>
        }
        required
      >
        <Input value={config.bot_token ?? ""} onChange={(e) => setField("bot_token", e.target.value)} required placeholder="123456:ABC-DEF..." />
      </Field>
      <Field
        label={
          <>
            Chat ID
            <Tip label="Chat ID">
              Grupo privado começa com <code>-100</code>; conversa direta com uma
            pessoa é só o ID numérico dela.
            </Tip>
          </>
        }
        required
      >
        <Input value={config.chat_id ?? ""} onChange={(e) => setField("chat_id", e.target.value)} required placeholder="-1001234567890" />
      </Field>
    </>
  );
}

function WhatsAppFields({ config, setField }: { config: Record<string,string>; setField: (k: string, v: string) => void }) {
  // Validação visual do formato E.164 (sem validar deliverability).
  // Regex permissiva: + seguido de 10-15 dígitos.
  const phone = config.to_phone ?? "";
  const phoneValid = phone === "" || /^\+\d{10,15}$/.test(phone);

  return (
    <>
      <Field
        label={
          <>
            Telefone do destinatário
            <Tip label="Formato do telefone">
              Formato internacional E.164: código do país, DDD e número, sem
              espaços nem traços.
            </Tip>
          </>
        }
        required
      >
        <Input
          value={phone}
          onChange={(e) => setField("to_phone", e.target.value)}
          required
          pattern="^\+\d{10,15}$"
          placeholder="+5511999998888"
        />
      </Field>
      {phone && !phoneValid && (
        <div className="text-[12px] text-red-600 dark:text-red-400">
          Formato inválido. Use E.164: começa com +, sem espaços ou parênteses.
        </div>
      )}
    </>
  );
}

function VoiceFields({ config, setField }: { config: Record<string,string>; setField: (k: string, v: string) => void }) {
  const bruto = config.para ?? "";
  const linhas = bruto.split(/[\n,;]/).map((s) => s.trim()).filter(Boolean);
  const invalidos = linhas.filter((n) => !/^\+\d{10,15}$/.test(n));

  return (
    <>
      <Field
        label={
          <>
            Números, em ordem de escalação
            <Tip label="Ordem dos números">
              Um por linha, em formato E.164 (+55 e o número, sem espaços). A
              ligação vai para o primeiro; se ninguém digitar 1, vai para o
              seguinte. Depois do último, aguarda a pausa e recomeça, até
              alguém reconhecer o alerta — pelo telefone ou aqui no painel.
            </Tip>
          </>
        }
        required
      >
        <textarea
          value={bruto}
          onChange={(e) => setField("para", e.target.value)}
          required
          rows={4}
          spellCheck={false}
          placeholder={"+5511999998888\n+5511888887777"}
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 font-mono text-sm text-slate-900 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-600"
        />
      </Field>

      {linhas.length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/50">
          <div className="mb-2 text-[12px] font-medium text-slate-600 dark:text-slate-400">
            Ordem em que o telefone vai tocar
          </div>
          <ol className="space-y-1">
            {linhas.map((n, i) => (
              <li key={`${n}-${i}`} className="flex items-center gap-2 font-mono text-sm">
                <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-200 text-[11px] font-semibold text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                  {i + 1}
                </span>
                <span className={/^\+\d{10,15}$/.test(n)
                  ? "text-slate-900 dark:text-slate-100"
                  : "text-red-600 line-through dark:text-red-400"}>
                  {n}
                </span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {invalidos.length > 0 && (
        <div className="text-[12px] text-red-600 dark:text-red-400">
          {invalidos.length === 1 ? "Um número será ignorado" : `${invalidos.length} números serão ignorados`}:
          use E.164, começando com + e sem espaços. Número fora do formato não
          é recusado pela operadora na hora do cadastro — ele falha na hora da
          ligação, que é de madrugada.
        </div>
      )}

      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-[12px] text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
        Este canal nasce inscrito <strong>somente em alertas críticos</strong>.
        Para mudar isso — ou desligar a ligação para uma regra específica —
        use a aba <strong>Inscrições</strong>. O limite de ligações por dia
        fica no cadastro do cliente.
      </div>
    </>
  );
}

// ---------- helpers ---------------------------------------------------------

function parseConfig(kind: ChannelKind, raw: Record<string, string>): Record<string, unknown> {
  const cfg: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(raw)) {
    if (v === "" || v === undefined || v === null) continue;
    cfg[k] = v;
  }
  // Coerce number/bool
  if (kind === "email") {
    if (typeof cfg.to_addrs === "string") {
      cfg.to_addrs = cfg.to_addrs.split(",").map((s) => s.trim()).filter(Boolean);
    }
    // Canal antigo editado depois da v38: o formulário não mostra mais as
    // chaves de SMTP, mas elas voltariam intactas no `config` carregado. Sair
    // daqui com `password` dentro é regravar no banco a senha que a migração
    // acabou de tirar de lá.
    for (const k of ["smtp_host", "smtp_port", "username", "password",
                     "use_tls", "from_addr"]) {
      delete cfg[k];
    }
  }
  if (kind === "voice" && typeof cfg.para === "string") {
    // Uma por linha, e a ORDEM é preservada: ela é a escalação.
    // Vírgula e ponto-e-vírgula também separam — quem cola de uma planilha
    // ou de uma mensagem não deveria perder a lista por causa disso.
    cfg.para = String(cfg.para)
      .split(/[\n,;]/)
      .map((s) => s.trim())
      .filter(Boolean);
  }
  if (kind === "webhook" && typeof cfg.headers === "string") {
    try {
      cfg.headers = JSON.parse(cfg.headers);
    } catch {
      // Mantém string mesmo
    }
  }
  return cfg;
}
